import { stockRefusal } from './shop'

/**
 * THE INTERACTIVE PARTS OF THE STORE GUIDES (moved from lib/shopGuide.ts by
 * 339, HLP-45). The guides' words are markdown now (guides/*.md); these are
 * the pure rules behind the two widgets a guide draws with `::practice-shelf`
 * and `::item-kinds` (components/guides/GuideWidgets.tsx). Their refusal is
 * the Stock tab's own `stockRefusal`, so a guide cannot teach a rule the
 * screen does not enforce.
 */

/* -----------------------------------------------------------------------------
   The practice shelf. Example rows, nothing sent anywhere. Its refusal is the
   Stock tab's own `stockRefusal`, so the guide cannot teach a rule the screen
   does not enforce. Pure, so the suite calls it directly.
   -------------------------------------------------------------------------- */

export type PracticeRow = {
  item: string
  colour: string
  size: string
  onShelf: number
  assigned: number
  waiting: number
  reorderAt: number | null
}

export const PRACTICE_ROWS: PracticeRow[] = [
  { item: 'Softshell jacket', colour: 'Navy', size: 'M', onShelf: 6, assigned: 2, waiting: 0, reorderAt: 3 },
  { item: 'Softshell jacket', colour: 'Navy', size: 'L', onShelf: 1, assigned: 1, waiting: 3, reorderAt: 2 },
  { item: 'Softshell jacket', colour: 'Grey', size: 'M', onShelf: 4, assigned: 0, waiting: 0, reorderAt: 2 },
  { item: 'Travel mug', colour: '—', size: '—', onShelf: 20, assigned: 5, waiting: 0, reorderAt: 6 },
]

export function practiceFree(r: PracticeRow): number {
  return r.onShelf - r.assigned
}

export function practiceIsLow(r: PracticeRow): boolean {
  return r.reorderAt !== null && practiceFree(r) <= r.reorderAt
}

export type PracticeOutcome =
  | { ok: false; message: string }
  | { ok: true; row: PracticeRow; filled: number; message: string }

function labelOf(r: PracticeRow): string {
  return r.colour === '—' ? r.item : `${r.item} · ${r.colour} · ${r.size}`
}

/**
 * Save a typed count the way the Stock tab does: a count, never a change;
 * never below what is promised; and stock that arrives serves the queue,
 * oldest first (069's claim).
 */
export function practiceSave(r: PracticeRow, typed: string): PracticeOutcome {
  const raw = typed.trim()
  if (/^[+-]\d/.test(raw)) {
    return { ok: false, message: `“${raw}” is a change, not a count. Type what is actually on the shelf.` }
  }
  if (!/^\d+$/.test(raw)) return { ok: false, message: 'A count is a whole number, zero or more.' }
  const n = Number(raw)
  const refusal = stockRefusal(n, r.assigned)
  if (refusal) return { ok: false, message: refusal }
  let assigned = r.assigned
  let waiting = r.waiting
  let filled = 0
  while (waiting > 0 && n - assigned > 0) {
    assigned++
    waiting--
    filled++
  }
  const row = { ...r, onShelf: n, assigned, waiting }
  const head = `${labelOf(r)}: on shelf ${r.onShelf} → ${n}.`
  const tail = filled
    ? ` ${filled} waiting ${filled === 1 ? 'order is' : 'orders are'} now ready, oldest first. Each person gets a notice and a pickup code.`
    : ''
  return { ok: true, row, filled, message: head + tail }
}

/** A collection: the unit leaves On shelf and Assigned together. */
export function practiceHandOver(r: PracticeRow): PracticeRow {
  if (r.assigned < 1) return r
  return { ...r, onShelf: r.onShelf - 1, assigned: r.assigned - 1 }
}

/* -----------------------------------------------------------------------------
   The four kinds of item. There is no pre-order switch (069): two settings,
   four behaviours, and this is the table of them.
   -------------------------------------------------------------------------- */

export type ItemKind = 'shelf' | 'window' | 'waiting' | 'preorder'

export function itemKind(takesOrdersWithNoStock: boolean, hasCloseDate: boolean): ItemKind {
  if (takesOrdersWithNoStock) return hasCloseDate ? 'preorder' : 'waiting'
  return hasCloseDate ? 'window' : 'shelf'
}

export const ITEM_KIND: Record<ItemKind, { name: string; line: string }> = {
  shelf: { name: 'Shelf stock only', line: 'Buy it if it is on the shelf.' },
  window: { name: 'Limited window', line: 'Stock we hold, until a date.' },
  waiting: { name: 'Waiting list', line: 'Order now, collect when it lands.' },
  preorder: { name: 'Pre-order', line: 'Collect orders, then buy from the supplier.' },
}
